import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import {
  border, textPrimary, textSecondary, textMuted, font, success, warning, error as errorColor,
} from "../../../layouts/schoolPortal/schoolPortalTheme";
import { card, label as labelStyle } from "./recordTheme";
import { get_student_knowledge_api } from "../../../DAL/SchoolPortal/SchoolPortal";

/**
 * The exam picture — what the student's Adapt+ page tells them, shown to their
 * instructor.
 *
 * Per paper: the estimated exam score (a range, because it is an estimate),
 * the chance they would pass if they sat it today, and a plain verdict. Open a
 * paper for its weakest areas — the things to raise in the next session — and
 * the earliest date they could sit it on current pace.
 *
 * The numbers come from the same knowledge model the student sees
 * (GET /api/school/students/:id/knowledge), so an instructor and their student
 * never read two different verdicts. It is deliberately cautious: it would
 * rather say "not yet" to a student who would pass than "ready" to one who
 * would not.
 */

const pct = (x) => `${Math.round((x || 0) * 100)}%`;

const verdict = (s) => {
  if (s.examReady) return { text: "Exam ready", color: success };
  if (!s.tested || s.answers < 20) return { text: "Too early", color: textMuted };
  if (s.passChance >= 0.6) return { text: "Close", color: warning };
  return { text: "Not ready", color: errorColor };
};

const AREA_COLOR = {
  "Exam standard": success, Solid: success, Building: warning, Weak: errorColor, "Early signs": textMuted, "Not tested": textMuted,
};

function Dots({ recent }) {
  if (!recent?.length) return null;
  return (
    <Box sx={{ display: "flex", gap: "3px" }} title="Last answers, oldest first">
      {recent.map((ok, i) => (
        <Box key={i} sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: ok ? success : errorColor, opacity: 0.85 }} />
      ))}
    </Box>
  );
}

function PaperDetail({ studentId, subject, first }) {
  const [state, setState] = useState({ loading: true, data: null });
  useEffect(() => {
    let cancelled = false;
    get_student_knowledge_api(studentId, subject).then((res) => {
      if (!cancelled) setState({ loading: false, data: res?.data?.subjects?.[subject] || null });
    }).catch(() => !cancelled && setState({ loading: false, data: null }));
    return () => { cancelled = true; };
  }, [studentId, subject]);

  if (state.loading) return <Box sx={{ py: 1.5, pl: 1 }}><CircularProgress size={14} /></Box>;
  const s = state.data;
  if (!s) return null;
  const weakest = (s.areas || []).filter((a) => a.labelText !== "Not tested").slice(0, 4);
  const untested = (s.areas || []).filter((a) => a.labelText === "Not tested");

  return (
    <Box sx={{ pl: 1, pr: 1, pb: 1.5, pt: 0.5 }}>
      {/* `short` only: the rest of the wording is addressed to the student. */}
      {s.readyDate?.short ? (
        <Typography sx={{ fontFamily: font, fontSize: "12px", color: textPrimary, mb: 1 }}>
          <b>Earliest exam date at {first}'s current pace:</b> {s.readyDate.short}
        </Typography>
      ) : null}
      <Typography sx={{ ...labelStyle, mb: 0.75 }}>Raise these with {first}</Typography>
      {weakest.map((a) => (
        <Box key={a.topic} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.5, borderTop: `1px solid ${border}` }}>
          <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textPrimary, flex: 1 }}>{a.name}</Typography>
          <Dots recent={a.recent} />
          <Typography sx={{ fontFamily: font, fontSize: "11px", color: textSecondary, width: 70, textAlign: "right" }}>
            {a.right}/{a.answers} right
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: "11px", fontWeight: 700, color: AREA_COLOR[a.labelText] || textSecondary, width: 92, textAlign: "right" }}>
            {a.labelText}
          </Typography>
        </Box>
      ))}
      {untested.length > 0 && (
        <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted, mt: 1 }}>
          Not practised yet: {untested.slice(0, 5).map((a) => a.name).join(", ")}{untested.length > 5 ? ` and ${untested.length - 5} more` : ""}.
        </Typography>
      )}
    </Box>
  );
}

/** The school has not opted in: say what Adapt+ would add, without the data. */
function NotOptedIn({ first }) {
  return (
    <Box sx={{ ...card, maxWidth: 900, mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontFamily: font, fontSize: "14px", fontWeight: 800, color: textPrimary }}>Exam picture</Typography>
        <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, mt: 0.25 }}>
          With Adapt+ you would see {first}'s estimated score and pass chance for every paper, the earliest date they could sit it, and the areas to raise next session.
        </Typography>
      </Box>
      <Typography sx={{ fontFamily: font, fontSize: "11px", fontWeight: 700, color: textMuted, border: `1px solid ${border}`, borderRadius: "999px", px: 1.25, py: 0.4, whiteSpace: "nowrap" }}>
        Adapt+ add-on · not on your plan
      </Typography>
    </Box>
  );
}

export default function RecordExamPicture({ studentId, studentName, adaptPlus = true, embedded = false }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [open, setOpen] = useState(null);
  const first = (studentName || "").split(" ")[0] || "They";

  useEffect(() => {
    if (!adaptPlus) return undefined;
    let cancelled = false;
    get_student_knowledge_api(studentId).then((res) => {
      if (cancelled) return;
      if (res?.code === 200) setState({ loading: false, data: res.data, error: null });
      else setState({ loading: false, data: null, error: res?.message || "Could not load" });
    }).catch(() => !cancelled && setState({ loading: false, data: null, error: "Could not load" }));
    return () => { cancelled = true; };
  }, [studentId, adaptPlus]);

  if (!adaptPlus) return <NotOptedIn first={first} />;
  if (state.loading) {
    return <Box sx={{ ...card, maxWidth: 900, mt: 2, display: "flex", alignItems: "center", gap: 1 }}><CircularProgress size={14} /><Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary }}>Working out the exam picture…</Typography></Box>;
  }
  if (!state.data) return null;
  const subjects = Object.entries(state.data.subjects || {});
  const ready = subjects.filter(([, s]) => s.examReady).length;

  return (
    <Box sx={embedded ? {} : { ...card, maxWidth: 900, mt: 2 }}>
      <Box sx={{ display: embedded ? "none" : "flex", alignItems: "baseline", justifyContent: "space-between", mb: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontFamily: font, fontSize: "14px", fontWeight: 800, color: textPrimary }}>Exam picture</Typography>
          {/* It is an add-on the school chose, so the page says so. */}
          <Typography sx={{ fontFamily: font, fontSize: "10.5px", fontWeight: 700, color: "#c01e2e", backgroundColor: "rgba(192,30,46,0.08)", borderRadius: "999px", px: 1, py: 0.3 }}>
            Adapt+ · your school's add-on
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary }}>
          {ready} of {subjects.length} papers exam ready · pass mark {state.data.passLine}%
        </Typography>
      </Box>
      <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted, mb: 1 }}>
        What {first} sees on their own Adapt+ page. Deliberately cautious — it would rather say "not yet" than "ready" too soon.
      </Typography>

      {subjects.map(([key, s]) => {
        const v = verdict(s);
        const isOpen = open === key;
        return (
          <Box key={key} sx={{ borderTop: `1px solid ${border}` }}>
            <Box
              onClick={() => setOpen(isOpen ? null : key)}
              sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, cursor: "pointer", "&:hover": { backgroundColor: "#FAF8F5" } }}
            >
              <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: textPrimary, width: 210, flexShrink: 0 }}>{s.label}</Typography>
              {/* The estimate as a bar: the range, with the pass line marked. */}
              <Box sx={{ position: "relative", flex: 1, height: 8, borderRadius: 4, backgroundColor: "#F1EEEA", minWidth: 80 }}>
                {s.tested ? (
                  <Box sx={{ position: "absolute", left: pct(s.estimate.low), width: `${Math.max(2, Math.round((s.estimate.high - s.estimate.low) * 100))}%`, top: 0, bottom: 0, borderRadius: 4, backgroundColor: v.color, opacity: 0.8 }} />
                ) : null}
                <Box sx={{ position: "absolute", left: `${state.data.passLine}%`, top: -3, bottom: -3, width: "2px", backgroundColor: textPrimary, opacity: 0.5 }} />
              </Box>
              <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, width: 74, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {s.tested ? `${Math.round(s.estimate.low * 100)}–${Math.round(s.estimate.high * 100)}%` : "—"}
              </Typography>
              <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, width: 74, textAlign: "right" }} title="Chance of passing if they sat it today">
                {s.tested ? `${pct(s.passChance)} pass` : ""}
              </Typography>
              <Typography sx={{ fontFamily: font, fontSize: "11.5px", fontWeight: 700, color: v.color, width: 78, textAlign: "right" }}>{v.text}</Typography>
            </Box>
            {/* Why, in one line, always — "88% right" next to "1% pass" is
                otherwise the first thing an instructor asks about. */}
            <Typography onClick={() => setOpen(isOpen ? null : key)} sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary, pb: 1, mt: -0.5, cursor: "pointer" }}>
              {s.line} <Box component="span" sx={{ color: "#c01e2e", fontWeight: 600 }}>{isOpen ? "Hide" : "What to raise →"}</Box>
            </Typography>
            {isOpen && <PaperDetail studentId={studentId} subject={key} first={first} />}
          </Box>
        );
      })}
    </Box>
  );
}
